import L from "leaflet";
import * as THREE from "three";

const STATE_KEY = "hydrotools:streetview-360:v1";

function esc(v) {
  return String(v ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;");
}

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}

function readState() {
  try {
    const raw = localStorage.getItem(STATE_KEY);
    const x = raw ? JSON.parse(raw) : {};
    return {
      selectedId: typeof x.selectedId === "string" ? x.selectedId : "",
      yawDeg: Number.isFinite(Number(x.yawDeg)) ? Number(x.yawDeg) : 0,
      pitchDeg: Number.isFinite(Number(x.pitchDeg)) ? Number(x.pitchDeg) : 0,
      zoomPct: Number.isFinite(Number(x.zoomPct)) ? Number(x.zoomPct) : 100,
      showMapDots: x.showMapDots !== false,
      showNumberLabels: x.showNumberLabels === true,
      showTrackline: x.showTrackline === true,
      brightnessPct: Number.isFinite(Number(x.brightnessPct)) ? clamp(Number(x.brightnessPct), 50, 150) : 100,
      contrastPct: Number.isFinite(Number(x.contrastPct)) ? clamp(Number(x.contrastPct), 50, 150) : 100,
      saturationPct: Number.isFinite(Number(x.saturationPct)) ? clamp(Number(x.saturationPct), 0, 200) : 100,
    };
  } catch {
    return {
      selectedId: "",
      yawDeg: 0,
      pitchDeg: 0,
      zoomPct: 100,
      showMapDots: true,
      showNumberLabels: false,
      showTrackline: false,
      brightnessPct: 100,
      contrastPct: 100,
      saturationPct: 100,
    };
  }
}

function saveState(x) {
  try { localStorage.setItem(STATE_KEY, JSON.stringify(x)); } catch {}
}

function normalizeDegrees(x) {
  const n = Number(x);
  if (!Number.isFinite(n)) return 0;
  let out = n % 360;
  if (out < 0) out += 360;
  return out;
}

function haversineDist(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function parseCoordText(raw, isLat) {
  const s = String(raw || "").trim();
  if (!s) return null;
  const direct = Number(s);
  if (Number.isFinite(direct)) return isLat ? clamp(direct, -90, 90) : clamp(direct, -180, 180);

  const hemi = (s.match(/[NSEW]$/i)?.[0] || "").toUpperCase();
  const nums = (s.match(/-?\d+(?:\.\d+)?/g) || []).map(Number).filter(Number.isFinite);
  if (!nums.length) return null;

  let val = nums[0];
  if (nums.length > 1) val += nums[1] / 60;
  if (nums.length > 2) val += nums[2] / 3600;

  if (hemi === "S" || hemi === "W") val = -Math.abs(val);
  if (hemi === "N" || hemi === "E") val = Math.abs(val);

  return isLat ? clamp(val, -90, 90) : clamp(val, -180, 180);
}

function findTagValue(text, tagNames) {
  for (const tag of tagNames) {
    const xml = new RegExp(`<${tag}>([^<]+)</${tag}>`, "i");
    const xmlM = text.match(xml);
    if (xmlM?.[1]) return xmlM[1].trim();
    const attr = new RegExp(`${tag}=\"([^\"]+)\"`, "i");
    const attrM = text.match(attr);
    if (attrM?.[1]) return attrM[1].trim();
  }
  return "";
}

function parseDateText(s) {
  const raw = String(s || "").trim();
  if (!raw) return "";
  const exifLike = raw.match(/^(\d{4}):(\d{2}):(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/);
  if (exifLike) {
    const iso = `${exifLike[1]}-${exifLike[2]}-${exifLike[3]}T${exifLike[4]}:${exifLike[5]}:${exifLike[6]}Z`;
    return iso;
  }
  const d = new Date(raw);
  return Number.isFinite(d.getTime()) ? d.toISOString() : "";
}

function readAscii(view, offset, count) {
  let out = "";
  for (let i = 0; i < count; i += 1) {
    const c = view.getUint8(offset + i);
    if (!c) break;
    out += String.fromCharCode(c);
  }
  return out.trim();
}

function parseExifGpsAndDate(buf) {
  const view = new DataView(buf);
  if (view.byteLength < 8 || view.getUint16(0, false) !== 0xffd8) return { lat: null, lon: null, ts: "" };
  let exifStart = -1;
  let exifLen = 0;
  let pos = 2;
  while (pos + 4 < view.byteLength) {
    if (view.getUint8(pos) !== 0xff) break;
    const marker = view.getUint8(pos + 1);
    if (marker === 0xda || marker === 0xd9) break;
    const segLen = view.getUint16(pos + 2, false);
    if (segLen < 2 || pos + 2 + segLen > view.byteLength) break;
    if (marker === 0xe1) {
      const id = readAscii(view, pos + 4, 6);
      if (id === "Exif") {
        exifStart = pos + 10;
        exifLen = segLen - 8;
        break;
      }
    }
    pos += 2 + segLen;
  }
  if (exifStart < 0 || exifLen <= 8) return { lat: null, lon: null, ts: "" };

  const tiff = exifStart;
  const endian = readAscii(view, tiff, 2);
  const le = endian === "II";
  if (!le && endian !== "MM") return { lat: null, lon: null, ts: "" };
  const u16 = (o) => view.getUint16(o, le);
  const u32 = (o) => view.getUint32(o, le);
  const typeSize = (t) => (t === 1 || t === 2 || t === 7) ? 1 : (t === 3) ? 2 : (t === 4 || t === 9) ? 4 : (t === 5 || t === 10) ? 8 : 0;

  function readEntryValue(type, count, valueOffsetField) {
    const bytes = typeSize(type) * count;
    const dataOffset = bytes <= 4 ? valueOffsetField : tiff + u32(valueOffsetField);
    if (dataOffset <= 0 || dataOffset + bytes > view.byteLength) return null;
    if (type === 2) return readAscii(view, dataOffset, count);
    if (type === 3 && count === 1) return u16(dataOffset);
    if (type === 4 && count === 1) return u32(dataOffset);
    if (type === 5) {
      const vals = [];
      for (let i = 0; i < count; i += 1) {
        const n = u32(dataOffset + i * 8);
        const d = u32(dataOffset + i * 8 + 4);
        vals.push(d ? (n / d) : 0);
      }
      return vals;
    }
    return null;
  }

  function readIfd(ifdOffset) {
    const out = new Map();
    const abs = tiff + ifdOffset;
    if (abs <= 0 || abs + 2 > view.byteLength) return out;
    const n = u16(abs);
    for (let i = 0; i < n; i += 1) {
      const e = abs + 2 + i * 12;
      if (e + 12 > view.byteLength) break;
      const tag = u16(e);
      const type = u16(e + 2);
      const count = u32(e + 4);
      const val = readEntryValue(type, count, e + 8);
      out.set(tag, { type, count, val, raw: e + 8 });
    }
    return out;
  }

  const ifd0 = readIfd(u32(tiff + 4));
  const gpsIfdPtr = ifd0.get(0x8825)?.val;
  const exifIfdPtr = ifd0.get(0x8769)?.val;
  const gpsIfd = Number.isFinite(Number(gpsIfdPtr)) ? readIfd(Number(gpsIfdPtr)) : new Map();
  const exifIfd = Number.isFinite(Number(exifIfdPtr)) ? readIfd(Number(exifIfdPtr)) : new Map();

  const latRef = String(gpsIfd.get(0x0001)?.val || "").trim().toUpperCase();
  const latVals = gpsIfd.get(0x0002)?.val;
  const lonRef = String(gpsIfd.get(0x0003)?.val || "").trim().toUpperCase();
  const lonVals = gpsIfd.get(0x0004)?.val;
  const dt = String(exifIfd.get(0x9003)?.val || ifd0.get(0x0132)?.val || "").trim();

  let lat = null;
  let lon = null;
  if (Array.isArray(latVals) && latVals.length >= 3) {
    lat = latVals[0] + latVals[1] / 60 + latVals[2] / 3600;
    if (latRef === "S") lat = -Math.abs(lat);
  }
  if (Array.isArray(lonVals) && lonVals.length >= 3) {
    lon = lonVals[0] + lonVals[1] / 60 + lonVals[2] / 3600;
    if (lonRef === "W") lon = -Math.abs(lon);
  }
  return { lat, lon, ts: parseDateText(dt) };
}

async function extractPhotoMeta(file) {
  const buf = await file.arrayBuffer();
  const exif = parseExifGpsAndDate(buf);
  const bytes = new Uint8Array(buf);
  const decoder = new TextDecoder("latin1");
  const head = decoder.decode(bytes.subarray(0, Math.min(bytes.length, 900000)));
  const tail = bytes.length > 900000 ? decoder.decode(bytes.subarray(Math.max(0, bytes.length - 900000))) : "";
  const text = `${head}\n${tail}`;

  const latRaw = findTagValue(text, [
    "exif:GPSLatitude",
    "GPS:GPSLatitude",
    "drone-dji:GPSLatitude",
    "drone-dji:Latitude",
    "GoPro:GPSLatitude",
    "Track1:GPSLatitude",
    "Track2:GPSLatitude",
    "Track3:GPSLatitude",
  ]);
  const lonRaw = findTagValue(text, [
    "exif:GPSLongitude",
    "GPS:GPSLongitude",
    "drone-dji:GPSLongitude",
    "drone-dji:Longitude",
    "GoPro:GPSLongitude",
    "Track1:GPSLongitude",
    "Track2:GPSLongitude",
    "Track3:GPSLongitude",
  ]);
  const dtRaw = findTagValue(text, [
    "exif:DateTimeOriginal",
    "xmp:CreateDate",
    "photoshop:DateCreated",
    "xmp:ModifyDate",
  ]);
  const headingRaw = findTagValue(text, [
    "GPano:PoseHeadingDegrees",
    "Track1:GPSImgDirection",
    "Track2:GPSImgDirection",
    "Track3:GPSImgDirection",
  ]);

  const lat = Number.isFinite(exif.lat) ? exif.lat : parseCoordText(latRaw, true);
  const lon = Number.isFinite(exif.lon) ? exif.lon : parseCoordText(lonRaw, false);
  const ts = exif.ts || parseDateText(dtRaw);
  const heading = Number.isFinite(Number(headingRaw)) ? normalizeDegrees(Number(headingRaw)) : 0;

  return { lat, lon, ts, heading };
}

function timeLabel(iso) {
  if (!iso) return "--";
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d.toLocaleString() : iso;
}

function parseGpxTrack(text) {
  try {
    const doc = new DOMParser().parseFromString(String(text || ""), "application/xml");
    const pts = [];
    doc.querySelectorAll("trkpt").forEach((node) => {
      const lat = Number(node.getAttribute("lat"));
      const lon = Number(node.getAttribute("lon"));
      const t = node.querySelector("time")?.textContent?.trim() || "";
      const ms = t ? new Date(t).getTime() : NaN;
      if (Number.isFinite(lat) && Number.isFinite(lon) && Number.isFinite(ms)) {
        pts.push({ lat, lon, ms });
      }
    });
    if (pts.length < 2) return [];
    pts.sort((a, b) => a.ms - b.ms);
    const t0 = pts[0].ms;
    return pts.map((p) => ({ t: (p.ms - t0) / 1000, lat: p.lat, lon: p.lon }));
  } catch {
    return [];
  }
}

function interpolateTrack(track, tSec) {
  if (!Array.isArray(track) || !track.length || !Number.isFinite(tSec)) return null;
  if (tSec <= track[0].t) return { lat: track[0].lat, lon: track[0].lon };
  if (tSec >= track[track.length - 1].t) {
    const last = track[track.length - 1];
    return { lat: last.lat, lon: last.lon };
  }
  let lo = 0;
  let hi = track.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (track[mid].t <= tSec) lo = mid;
    else hi = mid;
  }
  const a = track[lo];
  const b = track[hi];
  const span = Math.max(0.000001, b.t - a.t);
  const u = clamp((tSec - a.t) / span, 0, 1);
  return {
    lat: a.lat + (b.lat - a.lat) * u,
    lon: a.lon + (b.lon - a.lon) * u,
  };
}

async function extractVideoTrackFromFile(file) {
  try {
    const rsp = await fetch("/api/streetview/extract-track", {
      method: "POST",
      headers: {
        "Content-Type": "application/octet-stream",
        "X-File-Name": String(file?.name || "video.360"),
      },
      body: file,
    });
    if (!rsp.ok) return [];
    const data = await rsp.json().catch(() => null);
    const track = Array.isArray(data?.track) ? data.track : [];
    return track.filter((p) => Number.isFinite(Number(p?.t)) && Number.isFinite(Number(p?.lat)) && Number.isFinite(Number(p?.lon))).map((p) => ({
      t: Number(p.t),
      lat: Number(p.lat),
      lon: Number(p.lon),
    }));
  } catch {
    return [];
  }
}

async function convert360ToMp4(file) {
  try {
    const rsp = await fetch("/api/streetview/convert-360", {
      method: "POST",
      headers: {
        "Content-Type": "application/octet-stream",
        "X-File-Name": String(file?.name || "video.360"),
      },
      body: file,
    });
    if (!rsp.ok) return null;
    const data = await rsp.json().catch(() => null);
    const b64 = String(data?.base64 || "");
    if (!b64) return null;
    const bin = atob(b64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) arr[i] = bin.charCodeAt(i);
    const blob = new Blob([arr], { type: "video/mp4" });
    if (!blob.size) return null;
    return {
      url: URL.createObjectURL(blob),
      path: String(data?.path || ""),
      bytes: Number(data?.bytes || blob.size || 0),
    };
  } catch {
    return null;
  }
}

export const streetView360Tool = {
  id: "streetview-360",
  name: "Street View 360",
  icon: "360",
  init(app) {
    this._rt = this._rt ?? createStreetView360Tool(app);
  },
  destroy() {
    this._rt?.destroy?.();
    this._rt = null;
  },
  renderPanel(app, containerEl) {
    if (!this._rt) this._rt = createStreetView360Tool(app);
    this._rt.mount(containerEl);
  },
};

export function createStreetView360Runtime(app) {
  return createStreetView360Tool(app);
}

function createStreetView360Tool(app) {
  const state = readState();
  const markerLayer = L.layerGroup();
  let routeLine = null;
  let activeTrackLine = null;
  let playheadMarker = null;
  let containerEl = null;
  let photos = [];
  const videoTracks = new Map();
  let lastConversionPath = "";
  let playbackTimer = null;
  let isPlaying = false;
  let mapClickHandler = null;
  let mapMoveHandler = null;
  let importProgress = { active: false, current: 0, total: 0, label: "" };
  let selectedIds = new Set();
  let editGpsMode = false;
  let gpsDirty = false;
  let gpsSaving = false;
  let gpsSaveStatus = "";
  let activeMarinaCode = "";
  let editDragOrigin = null; // { primaryId, startLatLng, allStartPositions: Map<id,{lat,lon}> }
  const photoMarkerMap = new Map(); // id → L.marker (populated in refreshMapOverlays)

  const viewer = {
    renderer: null,
    scene: null,
    camera: null,
    mesh: null,
    texture: null,
    videoEl: null,
    drag: null,
    resizeObs: null,
    handlers: [],
    zoomAnim: null,
  };

  function persist() {
    saveState({
      selectedId: state.selectedId,
      yawDeg: normalizeDegrees(state.yawDeg),
      pitchDeg: clamp(Number(state.pitchDeg) || 0, -85, 85),
      zoomPct: clamp(Number(state.zoomPct) || 100, 70, 250),
      showMapDots: state.showMapDots !== false,
      showNumberLabels: state.showNumberLabels === true,
      showTrackline: state.showTrackline === true,
      brightnessPct: clamp(Number(state.brightnessPct) || 100, 50, 150),
      contrastPct: clamp(Number(state.contrastPct) || 100, 50, 150),
      saturationPct: clamp(Number(state.saturationPct) || 100, 0, 200),
    });
  }

  function applyViewerFilters() {
    const canvas = viewer.renderer?.domElement;
    if (!canvas) return;
    const b = clamp(Number(state.brightnessPct) || 100, 50, 150);
    const c = clamp(Number(state.contrastPct) || 100, 50, 150);
    const s = clamp(Number(state.saturationPct) || 100, 0, 200);
    canvas.style.filter = `brightness(${b}%) contrast(${c}%) saturate(${s}%)`;
  }

  function currentPhoto() {
    return photos.find((p) => p.id === state.selectedId) || photos[0] || null;
  }

  function photoPositionKey(photo) {
    return String(photo?.sourceKey || photo?.name || photo?.id || "").trim();
  }

  function buildOverrideMap(overrides) {
    const entries = Array.isArray(overrides?.positions) ? overrides.positions : [];
    const out = new Map();
    entries.forEach((entry) => {
      const lat = Number(entry?.lat);
      const lon = Number(entry?.lon);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
      const value = { lat, lon };
      const key = String(entry?.key || "").trim();
      const name = String(entry?.name || "").trim();
      if (key) out.set(key, value);
      if (name) out.set(name, value);
    });
    return out;
  }

  function applyPositionOverrides(overrides) {
    const byKey = buildOverrideMap(overrides);
    if (!byKey.size) return 0;
    let applied = 0;
    photos.forEach((photo) => {
      const match = byKey.get(photoPositionKey(photo)) || byKey.get(String(photo.name || ""));
      if (!match) return;
      photo.lat = match.lat;
      photo.lon = match.lon;
      applied += 1;
    });
    return applied;
  }

  async function loadPositionOverrides(marinaCode) {
    const code = String(marinaCode || "").trim();
    if (!code) return null;
    try {
      const rsp = await fetch(`/api/marina/dock360-position-overrides?code=${encodeURIComponent(code)}`);
      if (!rsp.ok) return null;
      return await rsp.json().catch(() => null);
    } catch {
      return null;
    }
  }

  async function saveGpsPositions() {
    const code = String(activeMarinaCode || "").trim();
    if (!code) {
      gpsSaveStatus = "No DAM marina is linked for this photo set.";
      render();
      return;
    }
    const positions = photos
      .filter((photo) => Number.isFinite(photo.lat) && Number.isFinite(photo.lon) && (photo.sourceKey || photo.name))
      .map((photo) => ({
        key: String(photo.sourceKey || ""),
        name: String(photo.sourceName || photo.name || ""),
        lat: Number(photo.lat),
        lon: Number(photo.lon),
      }));
    if (!positions.length) {
      gpsSaveStatus = "No hosted photo positions to save.";
      render();
      return;
    }
    gpsSaving = true;
    gpsSaveStatus = "Saving GPS positions...";
    render();
    try {
      const rsp = await fetch("/api/marina/dock360-position-overrides", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, positions }),
      });
      const data = await rsp.json().catch(() => ({}));
      if (!rsp.ok || data?.ok === false) throw new Error(data?.error || `HTTP ${rsp.status}`);
      gpsDirty = false;
      gpsSaveStatus = `Saved ${Number(data?.count || positions.length)} GPS position${Number(data?.count || positions.length) === 1 ? "" : "s"}.`;
      app.ui.toast("Saved 360 GPS positions.");
    } catch (err) {
      gpsSaveStatus = `Save failed: ${String(err?.message || err)}`;
      app.ui.toast(gpsSaveStatus);
    } finally {
      gpsSaving = false;
      render();
    }
  }

  function setSelected(id, opts = {}) {
    const { panToMap = false } = opts;
    state.selectedId = String(id || "");
    if (panToMap) {
      const p = photos.find((x) => x.id === state.selectedId);
      if (p && Number.isFinite(p.lat) && Number.isFinite(p.lon)) {
        app.map.instance.panTo([p.lat, p.lon], { animate: true, duration: 0.3 });
      }
    }
    persist();
    refreshMapOverlays();
    if (isPlaying) {
      stopPlayback();
      startPlayback();
    }
    render();
  }

  function sortPhotos(items) {
    return [...items].sort((a, b) => {
      const ta = a.ts ? new Date(a.ts).getTime() : Number.MAX_SAFE_INTEGER;
      const tb = b.ts ? new Date(b.ts).getTime() : Number.MAX_SAFE_INTEGER;
      if (ta !== tb) return ta - tb;
      return String(a.name).localeCompare(String(b.name));
    });
  }

  // Returns Map<photoId, distMetersToNextPhoto> for geotagged photos in sorted order.
  // The last geotagged photo has no entry (no "next").
  function computeJumps() {
    const geo = photos.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon));
    const result = new Map();
    for (let i = 0; i < geo.length - 1; i++) {
      result.set(geo[i].id, haversineDist(geo[i].lat, geo[i].lon, geo[i + 1].lat, geo[i + 1].lon));
    }
    return result;
  }

  function selectNearestPhotoAt(latlng, maxPx = 16) {
    if (!latlng || !app?.map?.instance) return false;
    const geo = photos.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon));
    if (!geo.length) return false;
    const clickPt = app.map.instance.latLngToContainerPoint(latlng);
    let best = null;
    let bestDist = Number.POSITIVE_INFINITY;
    for (const p of geo) {
      const pt = app.map.instance.latLngToContainerPoint([p.lat, p.lon]);
      const dx = pt.x - clickPt.x;
      const dy = pt.y - clickPt.y;
      const d = Math.sqrt((dx * dx) + (dy * dy));
      if (d < bestDist) {
        bestDist = d;
        best = p;
      }
    }
    if (!best || bestDist > maxPx) return false;
    state.yawDeg = Number.isFinite(best.heading) ? best.heading : state.yawDeg;
    setSelected(best.id, { panToMap: false });
    return true;
  }

  function nearestPhotoDistancePx(latlng) {
    if (!latlng || !app?.map?.instance) return Number.POSITIVE_INFINITY;
    const geo = photos.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon));
    if (!geo.length) return Number.POSITIVE_INFINITY;
    const clickPt = app.map.instance.latLngToContainerPoint(latlng);
    let bestDist = Number.POSITIVE_INFINITY;
    for (const p of geo) {
      const pt = app.map.instance.latLngToContainerPoint([p.lat, p.lon]);
      const dx = pt.x - clickPt.x;
      const dy = pt.y - clickPt.y;
      const d = Math.sqrt((dx * dx) + (dy * dy));
      if (d < bestDist) bestDist = d;
    }
    return bestDist;
  }

  function refreshMapOverlays() {
    photoMarkerMap.clear();
    markerLayer.clearLayers();
    if (routeLine) {
      try { markerLayer.removeLayer(routeLine); } catch {}
      routeLine = null;
    }
    if (activeTrackLine) {
      try { markerLayer.removeLayer(activeTrackLine); } catch {}
      activeTrackLine = null;
    }
    if (playheadMarker) {
      try { markerLayer.removeLayer(playheadMarker); } catch {}
      playheadMarker = null;
    }
    const geo = photos.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon));
    const jumps = computeJumps();
    if (state.showMapDots !== false) {
      geo.forEach((p, idx) => {
        const isPrimary = p.id === state.selectedId;
        const isMultiSel = selectedIds.has(p.id);
        const isAnySel = isPrimary || isMultiSel;
        const jumpDist = jumps.get(p.id);
        const isAmber = jumpDist != null && jumpDist > 10 && jumpDist <= 50;
        const isRed = jumpDist != null && jumpDist > 50;

        if (editGpsMode) {
          // DivIcon marker — supports Leaflet dragging
          const bg = isAnySel ? "#22c55e" : isRed ? "#ef4444" : isAmber ? "#f59e0b" : "#38bdf8";
          const sz = isAnySel ? 14 : 10;
          const ring = isAnySel ? `box-shadow:0 0 0 3px rgba(34,197,94,0.5);` : "";
          const icon = L.divIcon({
            html: `<div style="width:${sz}px;height:${sz}px;border-radius:50%;background:${bg};border:2px solid rgba(255,255,255,0.9);cursor:move;${ring}"></div>`,
            className: "",
            iconSize: [sz, sz],
            iconAnchor: [sz / 2, sz / 2],
          });
          const m = L.marker([p.lat, p.lon], { icon, draggable: true, zIndexOffset: isAnySel ? 1000 : 0 });

          if (state.showNumberLabels) {
            m.bindTooltip(`${idx + 1}`, { direction: "top", offset: [0, -sz / 2 - 2], permanent: true, opacity: 0.9, className: "sv360-marker-num" });
          }

          m.on("click", (ev) => {
            try { if (ev?.originalEvent) ev.originalEvent.__htHandled = "sv360-anchor"; } catch {}
            try { L.DomEvent.stop(ev); } catch {}
            const ctrl = ev?.originalEvent?.ctrlKey || ev?.originalEvent?.metaKey;
            if (ctrl) {
              if (selectedIds.has(p.id)) selectedIds.delete(p.id);
              else selectedIds.add(p.id);
            } else {
              selectedIds.clear();
              state.yawDeg = Number.isFinite(p.heading) ? p.heading : state.yawDeg;
              setSelected(p.id, { panToMap: true });
              return;
            }
            refreshMapOverlays();
            render();
          });

          m.on("dragstart", () => {
            editDragOrigin = {
              primaryId: p.id,
              startLatLng: m.getLatLng(),
              allStartPositions: new Map(
                [...(selectedIds.size ? selectedIds : [p.id])]
                  .map((id) => {
                    const ph = photos.find((x) => x.id === id);
                    return ph && Number.isFinite(ph.lat) ? [id, { lat: ph.lat, lon: ph.lon }] : null;
                  })
                  .filter(Boolean),
              ),
            };
            // Include the dragged marker itself if not already in selection
            if (!editDragOrigin.allStartPositions.has(p.id)) {
              editDragOrigin.allStartPositions.set(p.id, { lat: p.lat, lon: p.lon });
            }
          });

          m.on("drag", () => {
            if (!editDragOrigin || editDragOrigin.primaryId !== p.id) return;
            const cur = m.getLatLng();
            const dLat = cur.lat - editDragOrigin.startLatLng.lat;
            const dLng = cur.lng - editDragOrigin.startLatLng.lng;
            for (const [id, origin] of editDragOrigin.allStartPositions) {
              const ph = photos.find((x) => x.id === id);
              if (!ph) continue;
              ph.lat = origin.lat + dLat;
              ph.lon = origin.lon + dLng;
              if (id !== p.id) {
                photoMarkerMap.get(id)?.setLatLng([ph.lat, ph.lon]);
              }
            }
          });

          m.on("dragend", () => {
            const movedCount = editDragOrigin?.allStartPositions?.size ?? 1;
            editDragOrigin = null;
            gpsDirty = true;
            gpsSaveStatus = activeMarinaCode ? "Unsaved GPS edits." : "GPS edits are local until loaded from DAM.";
            app.log("360.gps_edited", { count: movedCount });
            refreshMapOverlays();
            render();
          });

          // Prevent mousedown from bubbling to map container and triggering map pan.
          // Leaflet's Draggable._dragging flag is supposed to block this, but is unreliable
          // when markers are frequently cleared/re-added. stopPropagation is the safe fallback.
          m.on("mousedown", (ev) => {
            try { if (ev?.originalEvent) ev.originalEvent.__htHandled = "sv360-anchor"; } catch {}
            try { if (ev?.originalEvent) L.DomEvent.stop(ev.originalEvent); } catch {}
            try { L.DomEvent.stop(ev); } catch {}
          });

          photoMarkerMap.set(p.id, m);
          markerLayer.addLayer(m);
        } else {
          // Normal mode — circleMarker
          const color = isAnySel ? "#22c55e" : isRed ? "#ef4444" : isAmber ? "#f59e0b" : "#38bdf8";
          const m = L.circleMarker([p.lat, p.lon], {
            radius: isAnySel ? 6 : 4,
            color,
            weight: 2,
            fillOpacity: 0.85,
            bubblingMouseEvents: false,
          });
          if (state.showNumberLabels) {
            m.bindTooltip(`${idx + 1}`, { direction: "top", offset: [0, -6], permanent: true, opacity: 0.9, className: "sv360-marker-num" });
          }
          m.on("click", (ev) => {
            try { if (ev?.originalEvent) ev.originalEvent.__htHandled = "sv360-anchor"; } catch {}
            try { if (ev?.originalEvent) L.DomEvent.stop(ev.originalEvent); } catch {}
            try { L.DomEvent.stop(ev); } catch {}
            const ctrl = ev?.originalEvent?.ctrlKey || ev?.originalEvent?.metaKey;
            if (ctrl) {
              if (selectedIds.has(p.id)) selectedIds.delete(p.id);
              else selectedIds.add(p.id);
              refreshMapOverlays();
              render();
            } else {
              selectedIds.clear();
              state.yawDeg = Number.isFinite(p.heading) ? p.heading : state.yawDeg;
              setSelected(p.id, { panToMap: true });
            }
          });
          m.on("mousedown", (ev) => {
            try { if (ev?.originalEvent) ev.originalEvent.__htHandled = "sv360-anchor"; } catch {}
            try { if (ev?.originalEvent) L.DomEvent.stop(ev.originalEvent); } catch {}
            try { L.DomEvent.stop(ev); } catch {}
          });
          photoMarkerMap.set(p.id, m);
          markerLayer.addLayer(m);
        }
      });
    }
    if (state.showTrackline && geo.length >= 2) {
      routeLine = L.polyline(geo.map((p) => [p.lat, p.lon]), {
        color: "#60a5fa",
        weight: 2,
        opacity: 0.8,
        dashArray: "6 4",
        interactive: false,
      });
      markerLayer.addLayer(routeLine);
    }
    const sel = currentPhoto();
    const trk = sel ? videoTracks.get(sel.id) : null;
    if (state.showTrackline && sel?.kind === "video" && Array.isArray(trk) && trk.length >= 2) {
      activeTrackLine = L.polyline(trk.map((p) => [p.lat, p.lon]), {
        color: "#a78bfa",
        weight: 3,
        opacity: 0.9,
        interactive: false,
      });
      markerLayer.addLayer(activeTrackLine);
      const here = interpolateTrack(trk, Number(viewer.videoEl?.currentTime) || 0);
      if (here) {
        playheadMarker = L.circleMarker([here.lat, here.lon], {
          radius: 7,
          color: "#f59e0b",
          weight: 2,
          fillOpacity: 0.95,
        });
        playheadMarker.on("click", (ev) => {
          try { if (ev?.originalEvent) ev.originalEvent.__htHandled = "sv360-anchor"; } catch {}
          try { if (ev?.originalEvent) L.DomEvent.stop(ev.originalEvent); } catch {}
          try { L.DomEvent.stop(ev); } catch {}
          setSelected(sel.id, { panToMap: false });
        });
        markerLayer.addLayer(playheadMarker);
      }
    }
  }

  function syncVideoTrackPosition() {
    const sel = currentPhoto();
    if (!sel || sel.kind !== "video") return;
    const trk = videoTracks.get(sel.id);
    if (!Array.isArray(trk) || !trk.length || !viewer.videoEl) return;
    const here = interpolateTrack(trk, Number(viewer.videoEl.currentTime) || 0);
    if (!here) return;
    if (!playheadMarker) {
      playheadMarker = L.circleMarker([here.lat, here.lon], {
        radius: 7,
        color: "#f59e0b",
        weight: 2,
        fillOpacity: 0.95,
      });
      playheadMarker.on("click", () => {
        setSelected(sel.id, { panToMap: false });
      });
      markerLayer.addLayer(playheadMarker);
    } else {
      playheadMarker.setLatLng([here.lat, here.lon]);
    }
  }

  async function importFiles(list, mode = "all", options = {}) {
    const onProgress = typeof options?.onProgress === "function" ? options.onProgress : null;
    const all = Array.from(list || []);
    const gpxFiles = all.filter((f) => /\.gpx$/i.test(String(f.name || "")));
    const files = all.filter((f) => {
      const n = String(f.name || "");
      const t = String(f.type || "");
      const isImg = /^image\//i.test(t) || /\.(jpe?g)$/i.test(n);
      const isVid = /^video\//i.test(t) || /\.(mp4|360)$/i.test(n);
      if (mode === "image") return isImg;
      if (mode === "video") return isVid || /\.gpx$/i.test(n);
      return isImg || isVid;
    });
    if (!files.length) {
      if (mode === "image") app.ui.toast("No images selected.");
      else if (mode === "video") app.ui.toast("No video files selected.");
      else app.ui.toast("No 360 images/videos selected.");
      return;
    }
    importProgress = { active: true, current: 0, total: files.length, label: "Preparing import..." };
    render();
    const next = [];
    for (let i = 0; i < files.length; i += 1) {
      const f = files[i];
      importProgress.current = i + 1;
      importProgress.label = `Loading ${f.name} (${i + 1}/${files.length})`;
      render();
      onProgress?.({
        phase: "processing",
        current: i + 1,
        total: files.length,
        name: String(f.name || ""),
      });
      const isVideo = /^video\//i.test(String(f.type || "")) || /\.(mp4|360)$/i.test(String(f.name || ""));
      const isRaw360 = /\.360$/i.test(String(f.name || ""));
      const source = f?._sv360Source && typeof f._sv360Source === "object" ? f._sv360Source : {};
      try {
        const meta = isVideo ? { lat: null, lon: null, ts: "", heading: 0 } : await extractPhotoMeta(f);
        const autoTrack = isVideo ? await extractVideoTrackFromFile(f) : [];
        const firstFix = autoTrack[0] || null;
        let mediaUrl = URL.createObjectURL(f);
        if (isRaw360) {
          const converted = await convert360ToMp4(f);
          if (converted?.url) {
            try { URL.revokeObjectURL(mediaUrl); } catch {}
            mediaUrl = converted.url;
            lastConversionPath = converted.path || "";
            app.ui.toast(`Converted ${f.name} to MP4 for smooth playback.`);
          } else {
            app.ui.toast(`Could not convert ${f.name}; using raw .360 playback.`);
          }
        }
        next.push({
          id: `${Date.now().toString(36)}-${i}-${Math.random().toString(16).slice(2, 7)}`,
          name: f.name,
          url: mediaUrl,
          kind: isVideo ? "video" : "image",
          ts: meta.ts || "",
          lat: Number.isFinite(meta.lat) ? meta.lat : (firstFix ? firstFix.lat : null),
          lon: Number.isFinite(meta.lon) ? meta.lon : (firstFix ? firstFix.lon : null),
          heading: Number.isFinite(meta.heading) ? meta.heading : 0,
          bytes: Number(f.size) || 0,
          autoTrack,
          sourceKey: String(source.key || ""),
          sourceName: String(source.name || f.name || ""),
          sourceMarina: String(source.marinaCode || ""),
        });
      } catch {
        next.push({
          id: `${Date.now().toString(36)}-${i}-${Math.random().toString(16).slice(2, 7)}`,
          name: f.name,
          url: URL.createObjectURL(f),
          kind: isVideo ? "video" : "image",
          ts: "",
          lat: null,
          lon: null,
          heading: 0,
          bytes: Number(f.size) || 0,
          autoTrack: [],
          sourceKey: String(source.key || ""),
          sourceName: String(source.name || f.name || ""),
          sourceMarina: String(source.marinaCode || ""),
        });
      }
    }
    photos = sortPhotos(next);
    activeMarinaCode = String(options?.marinaCode || photos.find((p) => p.sourceMarina)?.sourceMarina || "");
    applyPositionOverrides(options?.positionOverrides);
    gpsDirty = false;
    gpsSaving = false;
    gpsSaveStatus = activeMarinaCode ? "" : "GPS edits for local files are not shared.";
    videoTracks.clear();
    if (gpxFiles.length) {
      const allTracks = [];
      for (const gf of gpxFiles) {
        try {
          const txt = await gf.text();
          const trk = parseGpxTrack(txt);
          if (trk.length >= 2) allTracks.push(trk);
        } catch {}
      }
      if (allTracks.length) {
        const chosen = allTracks[0];
        photos.filter((p) => p.kind === "video").forEach((p) => {
          videoTracks.set(p.id, chosen);
        });
      }
    }
    photos.filter((p) => p.kind === "video" && Array.isArray(p.autoTrack) && p.autoTrack.length >= 2).forEach((p) => {
      if (!videoTracks.has(p.id)) videoTracks.set(p.id, p.autoTrack);
    });
    photos.forEach((p) => { delete p.autoTrack; });
    state.selectedId = photos[0]?.id || "";
    state.yawDeg = Number.isFinite(photos[0]?.heading) ? photos[0].heading : 0;
    state.zoomPct = 100;
    persist();
    refreshMapOverlays();

    const geoCount = photos.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon)).length;
    const trackCount = photos.filter((p) => videoTracks.has(p.id)).length;
    app.ui.toast(`Loaded ${photos.length} 360 file${photos.length === 1 ? "" : "s"} (${geoCount} geotagged photos, ${trackCount} video tracks).`);
    importProgress = { active: false, current: 0, total: 0, label: "" };
    render();
  }

  function clearPhotos() {
    stopPlayback();
    photos.forEach((p) => {
      try { URL.revokeObjectURL(p.url); } catch {}
    });
    photos = [];
    videoTracks.clear();
    gpsDirty = false;
    gpsSaving = false;
    gpsSaveStatus = "";
    activeMarinaCode = "";
    state.selectedId = "";
    state.yawDeg = 0;
    state.zoomPct = 100;
    persist();
    importProgress = { active: false, current: 0, total: 0, label: "" };
    refreshMapOverlays();
    render();
  }

  function shiftSelection(delta) {
    const selected = currentPhoto();
    if (selected?.kind === "video" && viewer.videoEl) {
      const v = viewer.videoEl;
      const step = 1;
      const dur = Number.isFinite(v.duration) ? v.duration : null;
      const next = (Number(v.currentTime) || 0) + (delta > 0 ? step : -step);
      if (dur && dur > 0) {
        v.currentTime = clamp(next, 0, Math.max(0, dur - 0.05));
      } else {
        v.currentTime = Math.max(0, next);
      }
      return;
    }
    if (!photos.length) return;
    const idx = photos.findIndex((p) => p.id === state.selectedId);
    const nextIdx = clamp((idx < 0 ? 0 : idx) + delta, 0, photos.length - 1);
    const p = photos[nextIdx];
    state.selectedId = p.id;
    if (Number.isFinite(p.heading)) state.yawDeg = p.heading;
    setSelected(p.id, { panToMap: true });
  }

  function fitRoute() {
    const sel = currentPhoto();
    const trk = sel ? videoTracks.get(sel.id) : null;
    if (Array.isArray(trk) && trk.length) {
      const b = L.latLngBounds(trk.map((p) => [p.lat, p.lon]));
      if (b.isValid()) app.map.instance.fitBounds(b.pad(0.15));
      return;
    }
    const geo = photos.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon));
    if (!geo.length) return;
    const b = L.latLngBounds(geo.map((p) => [p.lat, p.lon]));
    if (b.isValid()) app.map.instance.fitBounds(b.pad(0.15));
  }

  function stopPlayback() {
    isPlaying = false;
    if (playbackTimer) {
      clearInterval(playbackTimer);
      playbackTimer = null;
    }
    if (viewer.videoEl) {
      try { viewer.videoEl.pause(); } catch {}
    }
  }

  function startPlayback() {
    if (isPlaying || !photos.length) return;
    const selected = currentPhoto();
    if (selected?.kind === "video") {
      isPlaying = true;
      if (viewer.videoEl) {
        viewer.videoEl.play().catch(() => {});
      }
      return;
    }
    if (photos.length < 2) return;
    isPlaying = true;
    playbackTimer = setInterval(() => {
      if (!photos.length) {
        stopPlayback();
        return;
      }
      const idx = photos.findIndex((p) => p.id === state.selectedId);
      const nextIdx = (idx < 0 ? 0 : (idx + 1) % photos.length);
      const p = photos[nextIdx];
      setSelected(p.id, { panToMap: true });
    }, 900);
  }

  function togglePlayback() {
    if (isPlaying) stopPlayback();
    else startPlayback();
    render();
  }

  function zoomToFov(zoomPct) {
    const z = clamp(Number(zoomPct) || 100, 70, 250);
    const t = (z - 70) / (250 - 70);
    return 95 - t * 70;
  }

  function nextZoomLevel(currentZoom) {
    const levels = [100, 140, 190, 250];
    const z = clamp(Number(currentZoom) || 100, 70, 250);
    for (let i = 0; i < levels.length; i += 1) {
      if (z < levels[i] - 1) return levels[i];
      if (Math.abs(z - levels[i]) <= 1) return levels[(i + 1) % levels.length];
    }
    return levels[0];
  }

  function disposeViewer() {
    viewer.handlers.forEach(({ el, type, fn, opts }) => {
      try { el.removeEventListener(type, fn, opts); } catch {}
    });
    viewer.handlers = [];
    try { viewer.resizeObs?.disconnect?.(); } catch {}
    viewer.resizeObs = null;
    try { viewer.texture?.dispose?.(); } catch {}
    viewer.texture = null;
    if (viewer.videoEl) {
      try { viewer.videoEl.pause(); } catch {}
      try { viewer.videoEl.src = ""; } catch {}
    }
    viewer.videoEl = null;
    try { viewer.mesh?.geometry?.dispose?.(); } catch {}
    try { viewer.mesh?.material?.dispose?.(); } catch {}
    viewer.mesh = null;
    viewer.scene = null;
    viewer.camera = null;
    if (viewer.renderer) {
      try { viewer.renderer.dispose(); } catch {}
      try {
        const c = viewer.renderer.domElement;
        c?.parentElement?.removeChild?.(c);
      } catch {}
    }
    viewer.renderer = null;
    viewer.drag = null;
    if (viewer.zoomAnim?.rafId) {
      try { cancelAnimationFrame(viewer.zoomAnim.rafId); } catch {}
    }
    viewer.zoomAnim = null;
  }

  function syncCameraPose() {
    if (!viewer.camera || !viewer.renderer || !viewer.scene) return;
    const yaw = normalizeDegrees(state.yawDeg || 0);
    const pitch = clamp(Number(state.pitchDeg) || 0, -85, 85);
    viewer.camera.rotation.order = "YXZ";
    viewer.camera.rotation.y = THREE.MathUtils.degToRad(-yaw);
    viewer.camera.rotation.x = THREE.MathUtils.degToRad(pitch);
    viewer.camera.fov = zoomToFov(state.zoomPct);
    viewer.camera.updateProjectionMatrix();
    viewer.renderer.render(viewer.scene, viewer.camera);
  }

  function animateZoomTo(targetZoom, zoomInput) {
    const target = clamp(Number(targetZoom) || 100, 70, 250);
    const start = clamp(Number(state.zoomPct) || 100, 70, 250);
    if (!viewer.renderer || Math.abs(target - start) < 0.5) {
      state.zoomPct = target;
      if (zoomInput) zoomInput.value = String(Math.round(state.zoomPct));
      syncCameraPose();
      persist();
      return;
    }
    if (viewer.zoomAnim?.rafId) {
      try { cancelAnimationFrame(viewer.zoomAnim.rafId); } catch {}
    }
    const durationMs = 220;
    const t0 = performance.now();
    const easeOutCubic = (t) => 1 - ((1 - t) ** 3);
    const anim = { rafId: 0 };
    viewer.zoomAnim = anim;

    const step = (now) => {
      const p = clamp((now - t0) / durationMs, 0, 1);
      const eased = easeOutCubic(p);
      state.zoomPct = start + (target - start) * eased;
      if (zoomInput) zoomInput.value = String(Math.round(state.zoomPct));
      syncCameraPose();
      if (p < 1) {
        anim.rafId = requestAnimationFrame(step);
        return;
      }
      state.zoomPct = target;
      if (zoomInput) zoomInput.value = String(Math.round(state.zoomPct));
      syncCameraPose();
      persist();
      viewer.zoomAnim = null;
    };

    anim.rafId = requestAnimationFrame(step);
  }

  function addViewerHandler(el, type, fn, opts) {
    el.addEventListener(type, fn, opts);
    viewer.handlers.push({ el, type, fn, opts });
  }

  function mountViewer(hostEl, photo, yawInput, pitchInput, zoomInput) {
    disposeViewer();
    if (!hostEl || !photo) return;
    const w = Math.max(100, Math.floor(hostEl.clientWidth || 640));
    const h = Math.max(100, Math.floor(hostEl.clientHeight || 320));

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
    renderer.setSize(w, h, false);
    renderer.domElement.style.width = "100%";
    renderer.domElement.style.height = "100%";
    renderer.domElement.style.display = "block";
    renderer.domElement.style.cursor = "default";
    hostEl.appendChild(renderer.domElement);
    applyViewerFilters();

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(zoomToFov(state.zoomPct), w / h, 0.1, 2000);
    camera.position.set(0, 0, 0);

    const geom = new THREE.SphereGeometry(500, 96, 64);
    geom.scale(-1, 1, 1);
    let texture = null;
    if (photo.kind === "video") {
      const vid = document.createElement("video");
      vid.src = photo.url;
      vid.crossOrigin = "anonymous";
      vid.loop = true;
      vid.muted = true;
      vid.playsInline = true;
      vid.preload = "auto";
      viewer.videoEl = vid;
      texture = new THREE.VideoTexture(vid);
      texture.minFilter = THREE.LinearFilter;
      texture.magFilter = THREE.LinearFilter;
      texture.generateMipmaps = false;
      const onCanPlay = () => {
        if (isPlaying) vid.play().catch(() => {});
        else vid.pause();
        refreshMapOverlays();
        syncCameraPose();
      };
      vid.addEventListener("canplay", onCanPlay, { once: true });
      const onTime = () => {
        syncVideoTrackPosition();
        syncCameraPose();
      };
      vid.addEventListener("timeupdate", onTime);
      vid.load();
    } else {
      texture = new THREE.TextureLoader().load(photo.url, () => {
        syncCameraPose();
      });
      viewer.videoEl = null;
    }
    const mat = new THREE.MeshBasicMaterial({ map: texture });
    const mesh = new THREE.Mesh(geom, mat);
    scene.add(mesh);

    viewer.renderer = renderer;
    viewer.scene = scene;
    viewer.camera = camera;
    viewer.mesh = mesh;
    viewer.texture = texture;
    viewer.drag = null;

    const onDown = (ev) => {
      viewer.drag = {
        x: ev.clientX,
        y: ev.clientY,
        yaw: Number(state.yawDeg) || 0,
        pitch: Number(state.pitchDeg) || 0,
      };
      try { renderer.domElement.setPointerCapture(ev.pointerId); } catch {}
    };
    const onMove = (ev) => {
      if (!viewer.drag) return;
      const dx = ev.clientX - viewer.drag.x;
      const dy = ev.clientY - viewer.drag.y;
      state.yawDeg = normalizeDegrees(viewer.drag.yaw - dx * 0.15);
      state.pitchDeg = clamp(viewer.drag.pitch + dy * 0.15, -85, 85);
      if (yawInput) yawInput.value = String(Math.round(state.yawDeg));
      if (pitchInput) pitchInput.value = String(Math.round(state.pitchDeg));
      syncCameraPose();
    };
    const onUp = (ev) => {
      if (viewer.drag) persist();
      viewer.drag = null;
      try { renderer.domElement.releasePointerCapture(ev.pointerId); } catch {}
    };
    const onWheel = (ev) => {
      ev.preventDefault();
      const delta = ev.deltaY > 0 ? -5 : 5;
      state.zoomPct = clamp((Number(state.zoomPct) || 100) + delta, 70, 250);
      if (zoomInput) zoomInput.value = String(Math.round(state.zoomPct));
      syncCameraPose();
      persist();
    };
    const onDblClick = (ev) => {
      ev.preventDefault();
      animateZoomTo(nextZoomLevel(state.zoomPct), zoomInput);
    };

    addViewerHandler(renderer.domElement, "pointerdown", onDown);
    addViewerHandler(window, "pointermove", onMove);
    addViewerHandler(window, "pointerup", onUp);
    addViewerHandler(renderer.domElement, "wheel", onWheel, { passive: false });
    addViewerHandler(renderer.domElement, "dblclick", onDblClick);

    if (typeof ResizeObserver !== "undefined") {
      viewer.resizeObs = new ResizeObserver(() => {
        if (!viewer.renderer || !viewer.camera) return;
        const nw = Math.max(100, Math.floor(hostEl.clientWidth || 640));
        const nh = Math.max(100, Math.floor(hostEl.clientHeight || 320));
        viewer.renderer.setSize(nw, nh, false);
        viewer.camera.aspect = nw / nh;
        viewer.camera.updateProjectionMatrix();
        syncCameraPose();
      });
      viewer.resizeObs.observe(hostEl);
    }
    syncCameraPose();
  }

  function render() {
    if (!containerEl) return;
    const selected = currentPhoto();
    const geoCount = photos.filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon)).length;
    const isVideoSelected = selected?.kind === "video";
    const canPlay = isVideoSelected ? !!selected : photos.length > 1;
    const progressPct = importProgress.total > 0
      ? Math.max(0, Math.min(100, Math.round((importProgress.current / importProgress.total) * 100)))
      : 0;
    const rows = photos.map((p, i) => {
      const isRowSel = selectedIds.has(p.id) || p.id === state.selectedId;
      return `
      <tr data-sv360-select="${esc(p.id)}" style="cursor:pointer; background:${isRowSel ? "rgba(56,189,248,0.18)" : "transparent"};">
        <td style="padding:3px 6px;">${i + 1}</td>
        <td style="padding:3px 6px;">${esc(p.name)}${p.kind === "video" ? " [VID]" : ""}</td>
        <td style="padding:3px 6px;">${esc(timeLabel(p.ts))}</td>
        <td style="padding:3px 6px;">${Number.isFinite(p.lat) ? p.lat.toFixed(6) : "--"}</td>
        <td style="padding:3px 6px;">${Number.isFinite(p.lon) ? p.lon.toFixed(6) : "--"}</td>
      </tr>`;
    }).join("");

    const tlJumps = computeJumps();
    const tlN = photos.length;
    let timelineDots = "";
    if (!tlN) {
      timelineDots = `<span class="small" style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);opacity:0.45;">Import photos to begin.</span>`;
    } else {
      // Bin-based heatmap so 500+ photos don't smear into a solid bar.
      // Each bin is colored by the worst jump inside it.
      const NUM_BINS = 80;
      const bins = Array.from({ length: NUM_BINS }, () => ({ photos: [], maxJump: -1, hasGeo: false }));
      photos.forEach((p, i) => {
        const frac = tlN > 1 ? i / (tlN - 1) : 0.5;
        const b = Math.min(NUM_BINS - 1, Math.floor(frac * NUM_BINS));
        bins[b].photos.push(p);
        if (Number.isFinite(p.lat) && Number.isFinite(p.lon)) bins[b].hasGeo = true;
        const j = tlJumps.get(p.id);
        if (j != null) bins[b].maxJump = Math.max(bins[b].maxJump, j);
      });

      // Render bin segments
      const binW = (92 / NUM_BINS).toFixed(2);
      timelineDots = bins.map((bin, bi) => {
        if (!bin.photos.length) return "";
        const left = (4 + bi * 92 / NUM_BINS).toFixed(2);
        const bg = !bin.hasGeo
          ? "rgba(148,163,184,0.25)"
          : bin.maxJump > 50  ? "rgba(239,68,68,0.75)"
          : bin.maxJump > 10  ? "rgba(245,158,11,0.75)"
          : "rgba(56,189,248,0.55)";
        const tip = bin.photos.map((p) => {
          const j = tlJumps.get(p.id);
          return p.name + (j != null ? ` Δ${j >= 1000 ? (j / 1000).toFixed(1) + "km" : Math.round(j) + "m"}` : "");
        }).join("\n");
        return `<div style="position:absolute;left:${left}%;top:20%;width:${binW}%;height:60%;background:${bg};border-radius:2px;" title="${esc(tip)}"></div>`;
      }).join("");

      // Overlay selected-photo marker(s) on top of bins
      const selPhotos = photos.filter((p, i) => selectedIds.has(p.id) || p.id === state.selectedId
        ? (p._tlIdx = i, true) : false);
      selPhotos.forEach((p) => {
        const frac = tlN > 1 ? p._tlIdx / (tlN - 1) : 0.5;
        const left = (4 + frac * 92).toFixed(2);
        timelineDots += `<div data-sv360-tl="${esc(p.id)}" title="${esc(p.name)}"
          style="position:absolute;left:${left}%;top:50%;transform:translate(-50%,-50%);
          width:10px;height:10px;border-radius:50%;background:#22c55e;
          border:2px solid #fff;cursor:pointer;z-index:3;
          box-shadow:0 0 0 3px rgba(34,197,94,0.45);"></div>`;
        delete p._tlIdx;
      });

      // Overlay amber/red individual jump markers so glitch frames are pinpointed
      photos.forEach((p, i) => {
        const j = tlJumps.get(p.id);
        if (j == null || j <= 10) return;
        if (selectedIds.has(p.id) || p.id === state.selectedId) return; // already shown above
        const frac = tlN > 1 ? i / (tlN - 1) : 0.5;
        const left = (4 + frac * 92).toFixed(2);
        const bg = j > 50 ? "#ef4444" : "#f59e0b";
        const tip = `${p.name} | Δ${j >= 1000 ? (j / 1000).toFixed(1) + "km" : Math.round(j) + "m"} to next`;
        timelineDots += `<div data-sv360-tl="${esc(p.id)}" title="${esc(tip)}"
          style="position:absolute;left:${left}%;top:50%;transform:translate(-50%,-50%);
          width:8px;height:8px;border-radius:50%;background:${bg};
          border:1px solid rgba(255,255,255,0.8);cursor:pointer;z-index:2;"></div>`;
      });
    }
    const selCount = selectedIds.size;
    const selInfo = selCount > 0 ? `<span style="color:#22c55e;">${selCount} selected</span>` : "";
    const canSaveGps = !!activeMarinaCode && photos.some((p) => p.sourceKey && Number.isFinite(p.lat) && Number.isFinite(p.lon));

    containerEl.innerHTML = `
      <div class="card">
        <div class="lm-title">Street View 360</div>
        <div class="small" style="margin:6px 0 10px;">
          Dock-level 360 imagery viewer. Photos load automatically from the Digital Asset Manager.
        </div>
        <div class="row lm-add-row">
          <button id="sv360-clear" class="btn">Clear</button>
          <button id="sv360-prev" class="btn" title="${isVideoSelected ? "Back 1s" : "Previous"}" ${photos.length ? "" : "disabled"}>&#9664;</button>
          <button id="sv360-next" class="btn" title="${isVideoSelected ? "Forward 1s" : "Next"}" ${photos.length ? "" : "disabled"}>&#9654;</button>
          <button id="sv360-play" class="btn" title="${isVideoSelected ? "Play video" : "Play sequence"}" ${canPlay ? "" : "disabled"}>&#9658;</button>
          <button id="sv360-pause" class="btn" title="Pause sequence" ${isPlaying ? "" : "disabled"}>&#10074;&#10074;</button>
          <button id="sv360-fit" class="btn" ${geoCount ? "" : "disabled"}>Fit Route</button>
          <button id="sv360-edit-gps" class="btn" style="${editGpsMode ? "background:rgba(34,197,94,0.18);border-color:#22c55e;color:#22c55e;" : ""}" ${geoCount ? "" : "disabled"}>${editGpsMode ? "Stop Editing" : "Edit GPS"}</button>
          <button id="sv360-save-gps" class="btn" style="${gpsDirty ? "background:rgba(245,158,11,0.18);border-color:#f59e0b;color:#fbbf24;" : ""}" ${canSaveGps && gpsDirty && !gpsSaving ? "" : "disabled"}>${gpsSaving ? "Saving..." : "Save GPS"}</button>
        </div>
        <div class="small" style="margin-top:8px;">
          Frames: <strong>${photos.length}</strong> | Geotagged: <strong>${geoCount}</strong>${activeMarinaCode ? ` | Marina: <strong>${esc(activeMarinaCode)}</strong>` : ""}
        </div>
        ${gpsSaveStatus ? `<div class="small" style="margin-top:5px; color:${gpsDirty ? "#f59e0b" : "#22c55e"};">${esc(gpsSaveStatus)}</div>` : ""}
        ${importProgress.active ? `
          <div class="small" style="margin-top:8px;">${esc(importProgress.label || "Loading...")}</div>
          <div style="margin-top:6px; height:10px; border-radius:999px; background:rgba(148,163,184,0.25); overflow:hidden; border:1px solid rgba(148,163,184,0.35);">
            <div style="height:100%; width:${progressPct}%; background:linear-gradient(90deg,#22d3ee,#3b82f6); transition:width 120ms ease;"></div>
          </div>
          <div class="small" style="margin-top:4px;">${importProgress.current}/${importProgress.total} (${progressPct}%)</div>
        ` : ""}
        <div class="row" style="margin-top:8px; gap:12px; align-items:center;">
          <label class="small" style="display:flex; align-items:center; gap:6px;">
            <input id="sv360-show-map-dots" type="checkbox" ${state.showMapDots !== false ? "checked" : ""} />
            Show map dots
          </label>
          <label class="small" style="display:flex; align-items:center; gap:6px;">
            <input id="sv360-show-number-labels" type="checkbox" ${state.showNumberLabels ? "checked" : ""} />
            Show number labels
          </label>
          <label class="small" style="display:flex; align-items:center; gap:6px;">
            <input id="sv360-show-trackline" type="checkbox" ${state.showTrackline ? "checked" : ""} />
            Show trackline
          </label>
        </div>
        <div style="margin-top:10px; border-top:1px solid rgba(148,163,184,0.15); padding-top:8px;">
          <div style="display:flex; align-items:center; justify-content:space-between; margin-bottom:5px;">
            <div class="small" style="font-weight:600; letter-spacing:.03em; opacity:0.85;">GPS Timeline ${selInfo}</div>
            <div class="small" style="opacity:0.45; white-space:nowrap;">CTRL+click to multi-select</div>
          </div>
          ${editGpsMode ? `<div class="small" style="margin-bottom:5px; color:#f59e0b;">Edit mode active — drag markers on the map to reposition.</div>` : ""}
          <div id="sv360-timeline" style="position:relative; height:34px; background:rgba(148,163,184,0.08); border-radius:6px; overflow:visible; border:1px solid rgba(148,163,184,0.18);">
            ${timelineDots}
          </div>
          <div style="margin-top:4px; display:flex; gap:10px;">
            <span class="small" style="color:#38bdf8; opacity:0.8;">&#9679; Normal</span>
            <span class="small" style="color:#f59e0b; opacity:0.8;">&#9679; &gt;10m</span>
            <span class="small" style="color:#ef4444; opacity:0.8;">&#9679; &gt;50m</span>
          </div>
        </div>
        ${lastConversionPath ? `<div class="small" style="margin-top:4px; opacity:0.9;">Last MP4: <code>${esc(lastConversionPath)}</code></div>` : ""}
      </div>

      <div class="card" style="margin-top:8px; padding:8px;">
        <div style="display:flex; align-items:center; justify-content:space-between; gap:8px;">
          <div class="small"><strong>${selected ? esc(selected.name) : "No image selected"}</strong></div>
        </div>
        <div class="row" style="margin-top:8px; gap:10px; align-items:center; flex-wrap:wrap;">
          <label class="small" style="display:flex; align-items:center; gap:6px;">
            Brightness
            <input id="sv360-brightness" type="range" min="50" max="150" step="1" value="${Math.round(clamp(Number(state.brightnessPct) || 100, 50, 150))}" />
          </label>
          <label class="small" style="display:flex; align-items:center; gap:6px;">
            Contrast
            <input id="sv360-contrast" type="range" min="50" max="150" step="1" value="${Math.round(clamp(Number(state.contrastPct) || 100, 50, 150))}" />
          </label>
          <label class="small" style="display:flex; align-items:center; gap:6px;">
            Saturation
            <input id="sv360-saturation" type="range" min="0" max="200" step="1" value="${Math.round(clamp(Number(state.saturationPct) || 100, 0, 200))}" />
          </label>
          <button id="sv360-reset-image-controls" class="btn">Reset Image</button>
        </div>
        <div id="sv360-view" style="margin-top:8px; height:380px; border-radius:10px; border:1px solid rgba(148,163,184,0.35); background:#0b1220; overflow:hidden;" title="Drag to look around"></div>
      </div>

      <div class="card" style="margin-top:8px; padding:8px;">
        <div class="lm-title" style="margin:0 0 6px;">Frame Index</div>
        <div style="max-height:220px; overflow:auto;">
          <table style="width:100%; border-collapse:collapse; font-size:11px;">
            <thead>
              <tr style="text-align:left; border-bottom:1px solid rgba(148,163,184,0.3);">
                <th style="padding:3px 6px;">#</th>
                <th style="padding:3px 6px;">File</th>
                <th style="padding:3px 6px;">Time</th>
                <th style="padding:3px 6px;">Lat</th>
                <th style="padding:3px 6px;">Lon</th>
              </tr>
            </thead>
            <tbody>
              ${rows || `<tr><td colspan="5" class="small" style="padding:6px;">Import images to begin.</td></tr>`}
            </tbody>
          </table>
        </div>
      </div>
    `;

    const imageInput = containerEl.querySelector("#sv360-files-image");
    const videoInput = containerEl.querySelector("#sv360-files-video");
    const clearBtn = containerEl.querySelector("#sv360-clear");
    const prevBtn = containerEl.querySelector("#sv360-prev");
    const nextBtn = containerEl.querySelector("#sv360-next");
    const yawInput = null;
    const pitchInput = null;
    const zoomInput = null;
    const playBtn = containerEl.querySelector("#sv360-play");
    const pauseBtn = containerEl.querySelector("#sv360-pause");
    const fitBtn = containerEl.querySelector("#sv360-fit");
    const saveGpsBtn = containerEl.querySelector("#sv360-save-gps");
    const showMapDotsToggle = containerEl.querySelector("#sv360-show-map-dots");
    const showLabelsToggle = containerEl.querySelector("#sv360-show-number-labels");
    const showTracklineToggle = containerEl.querySelector("#sv360-show-trackline");
    const brightnessInput = containerEl.querySelector("#sv360-brightness");
    const contrastInput = containerEl.querySelector("#sv360-contrast");
    const saturationInput = containerEl.querySelector("#sv360-saturation");
    const resetImageControlsBtn = containerEl.querySelector("#sv360-reset-image-controls");
    const viewEl = containerEl.querySelector("#sv360-view");

    imageInput?.addEventListener("change", async (e) => {
      const files = e.target?.files;
      if (!files?.length) return;
      await importFiles(files, "image");
      e.target.value = "";
    });
    videoInput?.addEventListener("change", async (e) => {
      const files = e.target?.files;
      if (!files?.length) return;
      await importFiles(files, "video");
      e.target.value = "";
    });
    clearBtn?.addEventListener("click", clearPhotos);
    prevBtn?.addEventListener("click", () => shiftSelection(-1));
    nextBtn?.addEventListener("click", () => shiftSelection(1));
    playBtn?.addEventListener("click", () => { if (!isPlaying) togglePlayback(); });
    pauseBtn?.addEventListener("click", () => { if (isPlaying) togglePlayback(); });
    fitBtn?.addEventListener("click", fitRoute);
    saveGpsBtn?.addEventListener("click", () => { saveGpsPositions(); });
    showMapDotsToggle?.addEventListener("change", () => {
      state.showMapDots = !!showMapDotsToggle.checked;
      persist();
      refreshMapOverlays();
    });
    showLabelsToggle?.addEventListener("change", () => {
      state.showNumberLabels = !!showLabelsToggle.checked;
      persist();
      refreshMapOverlays();
    });
    showTracklineToggle?.addEventListener("change", () => {
      state.showTrackline = !!showTracklineToggle.checked;
      persist();
      refreshMapOverlays();
    });
    brightnessInput?.addEventListener("input", () => {
      state.brightnessPct = clamp(Number(brightnessInput.value) || 100, 50, 150);
      applyViewerFilters();
      persist();
    });
    contrastInput?.addEventListener("input", () => {
      state.contrastPct = clamp(Number(contrastInput.value) || 100, 50, 150);
      applyViewerFilters();
      persist();
    });
    saturationInput?.addEventListener("input", () => {
      state.saturationPct = clamp(Number(saturationInput.value) || 100, 0, 200);
      applyViewerFilters();
      persist();
    });
    resetImageControlsBtn?.addEventListener("click", () => {
      state.brightnessPct = 100;
      state.contrastPct = 100;
      state.saturationPct = 100;
      if (brightnessInput) brightnessInput.value = "100";
      if (contrastInput) contrastInput.value = "100";
      if (saturationInput) saturationInput.value = "100";
      applyViewerFilters();
      persist();
    });
    containerEl.querySelectorAll("[data-sv360-select]").forEach((rowEl) => {
      rowEl.addEventListener("click", () => setSelected(rowEl.getAttribute("data-sv360-select"), { panToMap: true }));
    });

    const editGpsBtn = containerEl.querySelector("#sv360-edit-gps");
    editGpsBtn?.addEventListener("click", () => {
      editGpsMode = !editGpsMode;
      if (!editGpsMode) editDragOrigin = null;
      refreshMapOverlays();
      render();
    });

    containerEl.querySelectorAll("[data-sv360-tl]").forEach((dot) => {
      dot.addEventListener("click", (ev) => {
        const id = dot.getAttribute("data-sv360-tl");
        if (ev.ctrlKey || ev.metaKey) {
          if (selectedIds.has(id)) selectedIds.delete(id);
          else selectedIds.add(id);
          refreshMapOverlays();
          render();
        } else {
          selectedIds.clear();
          const p = photos.find((x) => x.id === id);
          if (p) {
            state.yawDeg = Number.isFinite(p.heading) ? p.heading : state.yawDeg;
            setSelected(id, { panToMap: true });
          }
        }
      });
    });

    mountViewer(viewEl, selected, yawInput, pitchInput, zoomInput);
  }

  async function loadDamPhotos(marinaCode) {
    const code = String(marinaCode || "").trim();
    if (!code) return;
    try {
      const resp = await fetch(`/api/marina/assets?code=${encodeURIComponent(code)}&category=360-dock-photos`);
      if (!resp.ok) return;
      const data = await resp.json();
      const assetFiles = Array.isArray(data.files) ? data.files : [];
      if (!assetFiles.length) return;
      const presignResp = await fetch("/api/s3/presign/batch-download", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ keys: assetFiles.map((f) => f.key) }),
      });
      if (!presignResp.ok) return;
      const presignData = await presignResp.json();
      const urls = Array.isArray(presignData.urls) ? presignData.urls : [];
      const urlItems = assetFiles.map((f, i) => ({ url: urls[i] || "", name: f.name || `photo-${i + 1}.jpg`, key: f.key || "" })).filter((x) => x.url);
      if (!urlItems.length) return;
      // Reset photos so old marina's images don't persist
      photos.forEach((p) => { try { URL.revokeObjectURL(p.url); } catch {} });
      photos = [];
      markerLayer.clearLayers();
      render();
      const positionOverrides = await loadPositionOverrides(code);
      const concurrency = 8;
      let cursor = 0;
      const fileList = new Array(urlItems.length);
      async function worker() {
        while (true) {
          const idx = cursor++;
          if (idx >= urlItems.length) return;
          const item = urlItems[idx];
          const rsp = await fetch(item.url, { cache: "default" });
          if (!rsp.ok) continue;
          const blob = await rsp.blob();
          fileList[idx] = Object.assign(new File([blob], item.name, { type: blob.type || "image/jpeg" }), {
            _sv360Source: { key: item.key, name: item.name, marinaCode: code },
          });
        }
      }
      await Promise.all(Array.from({ length: concurrency }, worker));
      await importFiles(fileList.filter(Boolean), "image", { marinaCode: code, positionOverrides });
    } catch {}
  }

  function onMarinaChanged(e) {
    const marinaId = String(e.detail?.marinaId || "");
    if (marinaId) loadDamPhotos(marinaId).catch(() => {});
  }

  function mount(nextContainer) {
    containerEl = nextContainer;
    document.addEventListener("hydrotools:marina-changed", onMarinaChanged);
    app.map.instance.addLayer(markerLayer);
    if (!mapClickHandler) {
      mapClickHandler = (ev) => { try { selectNearestPhotoAt(ev?.latlng, 18); } catch {} };
      app.map.instance.on("click", mapClickHandler);
    }
    if (!mapMoveHandler) {
      mapMoveHandler = (ev) => {
        try {
          const d = nearestPhotoDistancePx(ev?.latlng);
          const el = app.map.instance.getContainer?.();
          if (!el) return;
          el.style.cursor = d <= 18 ? "crosshair" : "";
        } catch {}
      };
      app.map.instance.on("mousemove", mapMoveHandler);
    }
    render();
  }

  function destroy() {
    stopPlayback();
    disposeViewer();
    document.removeEventListener("hydrotools:marina-changed", onMarinaChanged);
    photos.forEach((p) => {
      try { URL.revokeObjectURL(p.url); } catch {}
    });
    photos = [];
    markerLayer.clearLayers();
    app.map.instance.removeLayer(markerLayer);
    if (mapClickHandler) {
      app.map.instance.off("click", mapClickHandler);
      mapClickHandler = null;
    }
    if (mapMoveHandler) {
      app.map.instance.off("mousemove", mapMoveHandler);
      mapMoveHandler = null;
    }
    try {
      const el = app.map.instance.getContainer?.();
      if (el) el.style.cursor = "";
    } catch {}
  }

  return {
    mount,
    destroy,
    importImageFiles: async (files, options = {}) => {
      const before = photos.length;
      await importFiles(files, "image", options);
      return Math.max(0, photos.length - before);
    },
    importHostedImageUrls: async (items, options = {}) => {
      const onProgress = typeof options?.onProgress === "function" ? options.onProgress : null;
      const list = Array.isArray(items) ? items : [];
      if (!list.length) return 0;
      const marinaCode = String(options?.marinaCode || list.find((item) => item?.marinaCode)?.marinaCode || "").trim();
      const positionOverrides = options?.positionOverrides || await loadPositionOverrides(marinaCode);
      const files = new Array(list.length);
      const concurrency = Math.max(2, Math.min(12, Number(options?.concurrency) || 8));
      let cursor = 0;
      let fetched = 0;
      async function worker() {
        while (true) {
          const idx = cursor;
          cursor += 1;
          if (idx >= list.length) return;
          const item = list[idx] || {};
          const url = String(item.url || "").trim();
          const name = String(item.name || `hosted-${idx + 1}.jpg`);
          if (!url) continue;
          // Allow browser HTTP cache so repeat marina loads don't re-download every image.
          const rsp = await fetch(url, { cache: "default" });
          if (!rsp.ok) throw new Error(`Failed to read hosted image (${rsp.status})`);
          const blob = await rsp.blob();
          files[idx] = new File([blob], name, { type: blob.type || "image/jpeg" });
          files[idx]._sv360Source = {
            key: String(item.key || ""),
            name,
            marinaCode,
          };
          fetched += 1;
          onProgress?.({
            phase: "fetching",
            current: fetched,
            total: list.length,
            name,
          });
        }
      }
      await Promise.all(Array.from({ length: concurrency }, () => worker()));
      const orderedFiles = files.filter(Boolean);
      const before = photos.length;
      await importFiles(orderedFiles, "image", { onProgress, marinaCode, positionOverrides });
      return Math.max(0, photos.length - before);
    },
    getImportProgress: () => ({ ...(importProgress || { active: false, current: 0, total: 0, label: "" }) }),
  };
}
